import React from 'react';
import { NswTerminationReason, NswTerminationTenancyType, nswTerminationRules } from '@app/config';
import { differenceInCalendarDays } from 'date-fns';
import Warning from '@app/components/dashboard/warnings/Warning';

interface NswNoticeDateWarningProps {
    tenancyType: NswTerminationTenancyType | undefined;
    dateOfNotice?: Date;
    vacantPossessionDate?: Date;
    reasonForTermination: NswTerminationReason | undefined;
}

export const validateTerminationNoticeDate = ({
    dateOfNotice,
    vacantPossessionDate,
    tenancyType,
    reasonForTermination
}: {
    dateOfNotice?: Date;
    vacantPossessionDate?: Date;
    tenancyType?: NswTerminationTenancyType;
    reasonForTermination?: NswTerminationReason;
}) => {
    // Validation result object
    const result = {
        isValid: true,
        message: ''
    };

    // Skip validation if missing required values
    if (!dateOfNotice || !vacantPossessionDate || !tenancyType || !reasonForTermination) {
        return result;
    }

    // Find the rule for the current tenancy type and reason
    const rule = nswTerminationRules[tenancyType]?.[reasonForTermination];
    if (rule) {
        const daysDifference = differenceInCalendarDays(vacantPossessionDate, dateOfNotice);
        if (daysDifference < rule.daysRequired) {
            return {
                isValid: false,
                message: rule.message
            };
        }
        return result; // Found a matching rule and it passed validation
    }

    return result;
};

const NswNoticeDateWarning: React.FC<NswNoticeDateWarningProps> = ({
    tenancyType,
    dateOfNotice,
    vacantPossessionDate,
    reasonForTermination
}) => {
    // Check if we need to show a warning using the standalone validation function
    const validationResult = React.useMemo(() => {
        const result = validateTerminationNoticeDate({
            dateOfNotice,
            vacantPossessionDate,
            tenancyType,
            reasonForTermination
        });

        return {
            showWarning: !result.isValid,
            message: result.message
        };
    }, [tenancyType, dateOfNotice, vacantPossessionDate, reasonForTermination]);

    return validationResult.showWarning ? <Warning>{validationResult.message}</Warning> : null;
};

export default NswNoticeDateWarning;
